import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Container,
  FormControl,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  styled,
} from "@mui/material";
import ChatIcon from "@mui/icons-material/Chat";
import { Link, useNavigate } from "react-router-dom";
import {
  buildWhatsappSchemaSolicitudesSearch,
  fetchWhatsappSchemaSolicitudes,
  WHATSAPP_SCHEMA_SOLICITUDES_SORT,
} from "./whatsappSchemaSolicitudesApi";
import { withBogotaStart } from "./solicitudFechaHora";

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === "dark" ? "#1A2027" : "#fff",
  ...theme.typography.body2,
  padding: theme.spacing(1),
  textAlign: "center",
  color: theme.palette.text.secondary,
}));

const ButtonAdd = styled(Button)({
  flexGrow: 0,
  borderRadius: "20px",
  gap: "10px",
  backgroundColor: "#161e55",
  color: "#ffff",
  margin: "0px 1rem",
  "&:hover": {
    backgroundColor: "#A8C8AF",
  },
});

const titles = [
  "Comercio",
  "Cel Comercio",
  "Paso",
  "Nombre cliente",
  "Cédula",
  "Celular",
  "Fecha Inicio",
  "Hora Inicio",
  "Conversación",
];

const rowsPerPage = 20;

const listParams = (page, searcher) => ({
  page,
  sort: WHATSAPP_SCHEMA_SOLICITUDES_SORT,
  size: rowsPerPage,
  search: buildWhatsappSchemaSolicitudesSearch(searcher),
});

export default function SolicitudV4() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [numberPage, setNumberPage] = useState(0);
  const [searcher, setSearcher] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadPage = useCallback(async (page, query) => {
    setLoading(true);
    setError(null);
    try {
      const payload = await fetchWhatsappSchemaSolicitudes(listParams(page, query));
      setData(payload && { ...payload, content: (payload.content || []).map(withBogotaStart) });
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Error al cargar solicitudes.");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPage(numberPage, searcher);
  }, []);

  const handleUpdate = () => {
    loadPage(numberPage, searcher);
  };

  const handleChangePage = (_event, newPage) => {
    setNumberPage(newPage);
    loadPage(newPage, searcher);
  };

  const handleChangeSearcher = (event) => {
    setSearcher(event.target.value);
  };

  const handleSearcher = () => {
    setNumberPage(0);
    loadPage(0, searcher);
  };

  return (
    <React.Fragment>
      <br />
      <Box sx={{ width: "100%", marginTop: "1.7rem" }}>
        <Container>
          <Item />
          <Item>.</Item>
          <Grid
            container
            spacing={4}
            sx={{ justifyContent: "space-between", alignItems: "center" }}
          >
            <Grid item>
              <h1>Solicitudes (V4)</h1>
            </Grid>
            <Grid item sx={{ display: "flex", gap: "0.5rem", width: "50vw" }}>
              <Grid item xs={6}>
                <Box width="100%" mt={5} mb={2}>
                  <FormControl fullWidth>
                    <TextField
                      id="busqueda-v4"
                      label="Comercio, cliente, documento o celular"
                      value={searcher}
                      onChange={handleChangeSearcher}
                      variant="outlined"
                    />
                  </FormControl>
                </Box>
              </Grid>
              <Grid item>
                <Box textAlign="center" mb={8} mt={6.5}>
                  <ButtonAdd onClick={handleSearcher}>Buscar</ButtonAdd>
                </Box>
              </Grid>
              <Grid item>
                <Box textAlign="center" mb={8} mt={6.5}>
                  <ButtonAdd onClick={handleUpdate} disabled={loading}>
                    Actualizar
                  </ButtonAdd>
                </Box>
              </Grid>
              <Grid item>
                <Box textAlign="center" mb={8} mt={6.5}>
                  <ButtonAdd onClick={() => navigate("/homeAdmin/solicitudes")}>
                    Volver
                  </ButtonAdd>
                </Box>
              </Grid>
            </Grid>
          </Grid>

          {error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : null}

          <Box>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    {titles.map((title) => (
                      <TableCell key={title}>{title}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={titles.length}>Cargando…</TableCell>
                    </TableRow>
                  ) : (data?.content || []).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={titles.length}>No hay solicitudes.</TableCell>
                    </TableRow>
                  ) : (
                    (data?.content || []).map((item) => (
                      <TableRow key={item.uuid}>
                        <TableCell>{item.commerceName}</TableCell>
                        <TableCell>{item.cellphoneCommerce}</TableCell>
                        <TableCell>{item.paso}</TableCell>
                        <TableCell>{item.nameClient}</TableCell>
                        <TableCell>
                          <Link to={`/homeAdmin/solicitudes/v4/detail/${item.uuid}`}>
                            {item.documentNumber}
                          </Link>
                        </TableCell>
                        <TableCell>{item.cellphoneClient}</TableCell>
                        <TableCell>{item.startDate}</TableCell>
                        <TableCell>{item.startTime}</TableCell>
                        <TableCell>
                          <Tooltip title="Ver conversación">
                            <IconButton
                              size="small"
                              disabled={!item.documentNumber}
                              onClick={() =>
                                navigate(
                                  `/homeAdmin/solicitudes/v4/conversacion/cliente/${item.documentNumber}`,
                                  {
                                    state: {
                                      nameClient: item.nameClient,
                                      commerceName: item.commerceName,
                                      cellphoneClient: item.cellphoneClient,
                                      cellphoneCommerce: item.cellphoneCommerce,
                                    },
                                  }
                                )
                              }
                              sx={{ color: "#161e55" }}
                            >
                              <ChatIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              <TablePagination
                rowsPerPageOptions={[rowsPerPage]}
                component="div"
                count={data?.totalElements ?? 0}
                rowsPerPage={rowsPerPage}
                page={numberPage}
                onPageChange={handleChangePage}
              />
            </TableContainer>
          </Box>
        </Container>
      </Box>
    </React.Fragment>
  );
}
